<template>
  <div>
    <div class="pg-head">
      <div class="pg-ico"><span class="mdi mdi-cash-multiple"></span></div>
      <div>
        <div class="id-h1">{{ $t('finance.title') }}</div>
        <div class="pg-sub">{{ $t('finance.subtitle') }}</div>
      </div>
      <div class="grid-spacer"></div>
      <!-- ขอบเขตข้อมูลของทั้งหน้า — ต้องอยู่ตรงหัวเรื่องที่เห็นก่อนตัวเลขทุกตัว
           ไม่ใช่ซ่อนไว้ท้ายหน้า ไม่งั้นคนอ่านเทียบยอดกับใบเสร็จจริงแล้วงงว่าทำไมไม่ตรง -->
      <span v-if="statsSince" class="badge badge-neutral" style="align-self:center">
        <span class="mdi mdi-calendar-start"></span>
        {{ $t('finance.statsSinceBadge', { date: formatDateTH(statsSince) }) }}
      </span>
      <span class="badge badge-warning" style="align-self:center">
        <span class="mdi mdi-lock-outline"></span> {{ $t('finance.managerOnly') }}
      </span>
      <button class="id-btn id-btn-soft" :disabled="exporting" @click="exportCsv">
        <span class="mdi" :class="exporting ? 'mdi-loading mdi-spin' : 'mdi-download'"></span>
        {{ $t('finance.exportCsv') }}
      </button>
    </div>

    <!-- ตัวกรองช่วงเวลา -->
    <div class="fin-filters">
      <!-- พรีเซ็ตที่ทั้งช่วงจบไปก่อนวันเริ่มเก็บสถิติ กดไปก็ได้ 0 ทุกช่อง — ปิดไว้พร้อมบอกเหตุผล
           ดีกว่าปล่อยให้กดแล้วเจอหน้าว่างโดยไม่รู้ว่าเพราะขายไม่ได้หรือเพราะไม่มีข้อมูล -->
      <button v-for="p in presets" :key="p.key" class="fin-chip"
        :class="{ on: activePreset === p.key }"
        :aria-disabled="presetDisabled(p.key) ? 'true' : null"
        v-hint="presetDisabled(p.key) ? $t('finance.presetBeforeStats', { date: formatDateTH(statsSince) }) : ''"
        @click="applyPreset(p.key)">{{ $t('finance.preset.' + p.key) }}</button>
      <span class="fin-sep"></span>
      <id-date v-model="from" :mindate="statsSince" :placeholder="$t('finance.fromDate')"
        @update:model-value="onCustomRange"></id-date>
      <span class="t-cap">–</span>
      <id-date v-model="to" :mindate="statsSince" :placeholder="$t('finance.toDate')"
        @update:model-value="onCustomRange"></id-date>
    </div>

    <div v-if="statsSince" class="fin-note fin-note-scope">
      <span class="mdi mdi-calendar-start"></span>
      {{ $t('finance.statsSinceNote', { date: formatDateTH(statsSince) }) }}
    </div>

    <template v-if="loading">
      <div class="fin-stat-row cols4" style="margin-top:14px">
        <div class="scard" v-for="n in 4" :key="'sk' + n"><div class="id-skel" style="height:92px"></div></div>
      </div>
      <div class="scard" style="margin-top:14px"><div class="id-skel" style="height:200px"></div></div>
    </template>

    <template v-else-if="summary">
      <!-- ══ A. การเคลื่อนไหวในช่วงที่เลือก ══ -->
      <div class="fin-sec">
        <span class="mdi mdi-swap-horizontal"></span>
        {{ $t('finance.sectionFlow') }}
        <span class="t-cap">{{ formatDateTH(from) }} – {{ formatDateTH(to) }}</span>
      </div>
      <div class="fin-stat-row cols4 id-fade-in" :key="'flow-' + from + to">
        <!-- ยอดขาย (ลูกค้าจ่ายรวม = ค่าคอร์ส + ค่าธรรมเนียม — ฐานเดียวกับกราฟข้างล่างเป๊ะ ๆ) -->
        <div class="scard fin-click" role="button" tabindex="0" @click="openDetail('sales')"
          @keydown.enter="openDetail('sales')">
          <div class="acc" style="background:linear-gradient(90deg,var(--c-primary),var(--c-primary-light))"></div>
          <div class="row" style="justify-content:space-between">
            <span class="lbl">{{ $t('finance.salesTile') }}</span>
            <span class="mdi mdi-cart-outline" style="color:var(--c-primary);font-size:20px"></span>
          </div>
          <div class="val">{{ money(flow.sales.gross) }} ฿</div>
          <div class="t-cap">
            <span v-if="flow.sales.momChangePct !== null" class="badge" :class="momClass(flow.sales.momChangePct)">
              <span class="mdi" :class="momIcon(flow.sales.momChangePct)"></span>
              {{ Math.abs(flow.sales.momChangePct) }}% {{ $t('finance.vsPrevPeriod') }}
            </span>
          </div>
          <div v-if="flow.sales.feeAmount > 0" class="t-cap" style="margin-top:4px">
            {{ $t('finance.salesParts', { course: money(flow.sales.courseAmount), fee: money(flow.sales.feeAmount) }) }}
          </div>
          <!-- อธิบายช่องว่างระหว่างการ์ดนี้กับ "เงินเข้าบัญชี" ตรงจุดที่คนอ่านเห็นมันครั้งแรก
               (เจ้าของถามเองว่าทำไมสองการ์ดยอดไม่เท่ากัน) — คิดจากใบที่นับเป็นยอดขายในช่วงนี้
               แล้วยังไม่จ่ายเท่านั้น ไม่ได้เอา ยอดขาย − เงินเข้า มาลบกันซึ่งคนละฐานวัน -->
          <div v-if="flow.sales.unpaidAmount > 0" class="t-cap fin-warn" style="margin-top:4px">
            <span class="mdi mdi-timer-sand-empty"></span>
            {{ $t('finance.salesUnpaid', {
              amount: money(flow.sales.unpaidAmount), count: flow.sales.unpaidCount }) }}
          </div>
          <div class="t-cap" style="margin-top:4px">
            {{ flow.sales.count }} {{ $t('finance.unitPackages') }} ·
            {{ $t('finance.avgPerPackage') }} {{ money(flow.sales.avgPerPackage) }} ฿
          </div>
          <div v-if="flow.sales.estimatedCount" class="t-cap fin-warn">
            <span class="mdi mdi-chart-timeline-variant"></span>
            {{ $t('finance.estimatedPart', { amount: money(flow.sales.estimatedAmount), count: flow.sales.estimatedCount }) }}
          </div>
          <div v-if="flow.sales.missingPriceCount" class="t-cap fin-danger" style="cursor:pointer" @click.stop="openMissingPrice">
            <span class="mdi mdi-alert-circle-outline"></span>
            {{ $t('finance.missingPriceWarn', { count: flow.sales.missingPriceCount }) }}
          </div>
          <div v-if="flow.sales.unknownPriceCount" class="t-cap fin-warn">
            <span class="mdi mdi-help-circle-outline"></span>
            {{ $t('finance.unknownPriceWarn', { count: flow.sales.unknownPriceCount }) }}
          </div>
          <div class="fin-more"><span class="mdi mdi-magnify-plus-outline"></span> {{ $t('finance.clickDetail') }}</div>
        </div>

        <!-- เงินเข้าบัญชี (รวมค่าธรรมเนียม — เจ้าของยืนยัน 2026-08-04 ว่า fee เป็นรายรับด้วย
             เพราะอัตราที่บวกคิดเกินต้นทุนธนาคารจริงเล็กน้อย ไม่มี "สุทธิ" อีกแล้ว) -->
        <div class="scard fin-click" role="button" tabindex="0" @click="openDetail('collected')"
          @keydown.enter="openDetail('collected')">
          <div class="acc" style="background:linear-gradient(90deg,var(--c-success),var(--c-accent-2))"></div>
          <div class="row" style="justify-content:space-between">
            <span class="lbl">{{ $t('finance.collectedTile') }}</span>
            <span class="mdi mdi-wallet-outline" style="color:var(--c-success);font-size:20px"></span>
          </div>
          <div class="val">{{ money(flow.collected.gross) }} ฿</div>
          <div class="fin-bar" v-if="flow.collected.pctOfSales !== null">
            <div class="fin-bar-fill" :style="{ width: Math.min(flow.collected.pctOfSales, 100) + '%' }"></div>
          </div>
          <div class="t-cap">
            <template v-if="flow.collected.fee > 0">
              {{ $t('finance.collectedFeeIncluded', { fee: money(flow.collected.fee) }) }}
            </template>
            <template v-if="flow.collected.pctOfSales !== null">
              · {{ flow.collected.pctOfSales }}% {{ $t('finance.ofSales') }}
            </template>
          </div>
          <div v-if="flow.collected.feeUnknownCount" class="t-cap fin-danger">
            <span class="mdi mdi-alert-circle-outline"></span>
            {{ $t('finance.feeUnknownWarn', { count: flow.collected.feeUnknownCount }) }}
          </div>
          <div class="fin-more"><span class="mdi mdi-magnify-plus-outline"></span> {{ $t('finance.clickDetail') }}</div>
        </div>

        <!-- VAT ในเงินที่เก็บได้ (เจ้าของสั่ง 2026-08-04) — คิดจากยอดที่ลูกค้าจ่ายจริง
             ไม่หักค่าธรรมเนียม · ราคารวม VAT แล้วจึงถอดด้วย 7/107
             ⚠️ การ์ดนี้ "กดไม่ได้" โดยตั้งใจ (เจ้าของสั่ง 2026-08-04) — เดิมกดแล้วเปิด popup
             ที่มีตารางสรุปรายวันหน้าตาเหมือนของการ์ดเงินเข้าบัญชีเป๊ะ ๆ เพราะ VAT คิดจากฐาน
             เดียวกันและตารางนั้นมีคอลัมน์ VAT อยู่แล้ว = ทางเข้าซ้ำซ้อนสองทางไปที่ข้อมูลชุดเดียวกัน
             ใครอยากดู VAT รายวัน กดที่การ์ด "เงินเข้าบัญชี" ทางเดียว -->
        <div class="scard">
          <div class="acc" style="background:linear-gradient(90deg,var(--c-info),#93c5fd)"></div>
          <div class="row" style="justify-content:space-between">
            <span class="lbl">{{ $t('finance.vatTile', { pct: flow.vat.ratePct }) }}</span>
            <span class="mdi mdi-receipt-text-outline" style="color:var(--c-info);font-size:20px"></span>
          </div>
          <div class="val">{{ money(flow.vat.amount) }} ฿</div>
          <div class="t-cap" style="margin-top:8px">
            {{ $t('finance.vatFrom', { amount: money(flow.vat.base) }) }}
          </div>
          <div class="t-cap dim" style="margin-top:8px">
            <span class="mdi mdi-information-outline"></span> {{ $t('finance.vatSeeCollected') }}
          </div>
        </div>

        <!-- รายได้ตามบริการที่สอนจริง -->
        <div class="scard fin-click" role="button" tabindex="0" @click="openDetail('earned')"
          @keydown.enter="openDetail('earned')">
          <div class="acc" style="background:linear-gradient(90deg,var(--c-secondary),var(--c-secondary-light))"></div>
          <div class="row" style="justify-content:space-between">
            <span class="lbl">{{ $t('finance.earnedTile') }}</span>
            <span class="mdi mdi-checkbox-marked-outline" style="color:var(--c-secondary);font-size:20px"></span>
          </div>
          <div class="val">{{ money(flow.earned.amount) }} ฿</div>
          <div class="t-cap" style="margin-top:8px">
            {{ $t('finance.earnedFrom', { sessions: flow.earned.sessions }) }}
          </div>
          <div v-if="flow.earned.unpriceableSessions" class="t-cap fin-warn">
            <span class="mdi mdi-information-outline"></span>
            {{ $t('finance.earnedUnpriceable', { sessions: flow.earned.unpriceableSessions }) }}
          </div>
          <div class="fin-more"><span class="mdi mdi-magnify-plus-outline"></span> {{ $t('finance.clickDetail') }}</div>
        </div>
      </div>

      <!-- ══ B. ยอดคงค้าง ณ วันนี้ ══ -->
      <div class="fin-sec">
        <span class="mdi mdi-scale-balance"></span>
        {{ $t('finance.sectionStock') }}
        <span class="t-cap">{{ $t('finance.asOfToday') }} {{ formatDateTH(summary.period.today) }}</span>
      </div>
      <div class="fin-stat-row cols4 id-fade-in" :key="'stock-' + summary.generatedAt">
        <!-- ค้างรับคอร์สมีตารางเต็ม (อายุหนี้) อยู่ล่างสุดของหน้าอยู่แล้ว — กดแล้วพาไปที่ตาราง
             ไม่เปิด popup ซ้อนซึ่งจะได้ข้อมูลน้อยกว่าตารางจริง -->
        <div class="scard fin-click" role="button" tabindex="0" @click="scrollToReceivables"
          @keydown.enter="scrollToReceivables">
          <div class="acc" style="background:linear-gradient(90deg,var(--c-error),#fb7185)"></div>
          <div class="row" style="justify-content:space-between">
            <span class="lbl">{{ $t('finance.receivableTile') }}</span>
            <span class="mdi mdi-alert-outline" style="color:var(--c-error);font-size:20px"></span>
          </div>
          <div class="val">{{ money(stock.receivable.amount) }} ฿</div>
          <div class="t-cap" style="margin-top:8px">
            {{ stock.receivable.count }} {{ $t('finance.unitPackages') }}
            <span v-if="stock.receivable.usedSessionsCount" class="fin-danger">
              · {{ $t('finance.receivableUsed', { count: stock.receivable.usedSessionsCount }) }}
            </span>
          </div>
          <div v-if="stock.receivable.unknownPriceCount" class="t-cap fin-warn">
            <span class="mdi mdi-help-circle-outline"></span>
            {{ $t('finance.unknownPriceExcluded', { count: stock.receivable.unknownPriceCount }) }}
          </div>
          <div class="fin-more"><span class="mdi mdi-arrow-down"></span> {{ $t('finance.clickGoTable') }}</div>
        </div>

        <!-- ค้างรับ Private — การ์ดแยกของตัวเอง (เจ้าของสั่ง 2026-08-04)
             เดิมค้างรับนับแค่แพ็กเกจคอร์ส คาบ Private ที่ยังไม่จ่ายจึงเป็นเงินที่ลูกค้าติดค้าง
             แต่ไม่โผล่ในหน้านี้เลย · ไม่ยุบรวมกับใบข้างบนเพราะคนละแหล่งข้อมูลและคนละหน้าที่ต้องไปตามแก้ -->
        <div class="scard fin-click" role="button" tabindex="0" @click="openDetail('receivablePrivate')"
          @keydown.enter="openDetail('receivablePrivate')">
          <div class="acc" style="background:linear-gradient(90deg,var(--c-error),var(--c-secondary))"></div>
          <div class="row" style="justify-content:space-between">
            <span class="lbl">{{ $t('finance.receivablePrivateTile') }}</span>
            <span class="mdi mdi-account-clock-outline" style="color:var(--c-error);font-size:20px"></span>
          </div>
          <div class="val">{{ money(stock.receivablePrivate.amount) }} ฿</div>
          <div class="t-cap" style="margin-top:8px">
            {{ $t('finance.receivablePrivateCount', { count: stock.receivablePrivate.count }) }}
          </div>
          <!-- คาบที่สอนไปแล้วแต่ยังไม่ได้เงิน = ก้อนที่ต้องรีบตามที่สุด · ที่เหลือเป็นคาบอนาคต
               ซึ่งยังไม่ถือว่าเลยกำหนดชำระ จึงต้องแยกให้เห็น ไม่ปนเป็นก้อนเดียว -->
          <div v-if="stock.receivablePrivate.taughtCount" class="t-cap fin-danger">
            <span class="mdi mdi-alert-circle-outline"></span>
            {{ $t('finance.receivablePrivateTaught', {
              count: stock.receivablePrivate.taughtCount,
              amount: money(stock.receivablePrivate.taughtAmount) }) }}
          </div>
          <div class="fin-more"><span class="mdi mdi-magnify-plus-outline"></span> {{ $t('finance.clickDetail') }}</div>
        </div>

        <!-- ── อีเวนต์ (v3) — 3 ก้อนตามหลักบัญชีที่เจ้าของวาง (§7.7) ────────────────────
             ① รายได้ยิมจากอีเวนต์ = gym_fee เท่านั้น (ถูกรวมใน "ยอดขาย/เงินเข้า" ข้างบนแล้ว
                การ์ดนี้แค่แตกให้เห็นส่วนของอีเวนต์) + บรรทัดเงินคืนตามวันที่คืนจริง
             ② ค้างรับอีเวนต์ = ยอดที่ลูกค้ายังไม่จ่าย (ทั้งก้อนสองขา) แยกใบจากค้างรับคอร์ส/Private
             ③ เงินรับแทนผู้จัด = **เจ้าหนี้อื่น ไม่ใช่รายได้** ปนเมื่อไหร่รายได้บวม + VAT ผิด
             การ์ดขึ้นเฉพาะเมื่อมีข้อมูลจริง — ยิมที่ยังไม่จัดอีเวนต์จะไม่เห็นการ์ดว่างเปล่า -->
        <div v-if="hasEventMoney" class="scard">
          <div class="acc" style="background:linear-gradient(90deg,var(--c-primary),var(--c-accent))"></div>
          <div class="row" style="justify-content:space-between">
            <span class="lbl">{{ $t('finance.eventRevenueTile') }}</span>
            <span class="mdi mdi-trophy-outline" style="color:var(--c-primary);font-size:20px"></span>
          </div>
          <div class="val">{{ money(flow.events.sales.gross) }} ฿</div>
          <div class="t-cap" style="margin-top:8px">
            {{ $t('finance.eventSalesCount', { count: flow.events.sales.count }) }} ·
            {{ $t('finance.eventCollected', { amount: money(flow.events.collected.gross) }) }}
          </div>
          <div v-if="flow.events.sales.unpaidCount" class="t-cap fin-warn">
            <span class="mdi mdi-alert-circle-outline"></span>
            {{ $t('finance.eventUnpaidInSales', {
              count: flow.events.sales.unpaidCount, amount: money(flow.events.sales.unpaidAmount) }) }}
          </div>
          <div v-if="flow.events.refunds.count" class="t-cap fin-danger">
            <span class="mdi mdi-cash-refund"></span>
            {{ $t('finance.eventRefunds', {
              count: flow.events.refunds.count, amount: money(flow.events.refunds.amount) }) }}
          </div>
          <!-- ใบที่ยกเลิกแบบ "ไม่คืนเงิน" — เงินอยู่ในบัญชียิมจริง ต้องเห็นในรายงาน ไม่หายเงียบ
               (เจ้าของเคาะ 12 ส.ค.) · แตกให้เห็นว่าเป็นค่ายิมเท่าไหร่ / ขาผู้จัดที่เก็บไว้เท่าไหร่ -->
          <div v-if="flow.events.forfeited && flow.events.forfeited.count" class="t-cap">
            <span class="mdi mdi-wallet-outline"></span>
            {{ $t('evV6.forfeitTile') }} {{ money(flow.events.forfeited.amount) }} ฿
            ({{ $t('evV6.forfeitCount', { count: flow.events.forfeited.count }) }})
            <template v-if="Number(flow.events.forfeited.organizerKeptAmount) > 0">
              — {{ $t('evV6.forfeitBreakdown', {
                gym: money(flow.events.forfeited.gymAmount),
                org: money(flow.events.forfeited.organizerKeptAmount) }) }}
            </template>
          </div>
          <div class="t-cap">{{ $t('finance.eventGymOnlyNote') }}</div>
        </div>

        <div v-if="hasEventMoney && stock.receivableEvent.count" class="scard">
          <div class="acc" style="background:linear-gradient(90deg,var(--c-error),var(--c-primary))"></div>
          <div class="row" style="justify-content:space-between">
            <span class="lbl">{{ $t('finance.eventReceivableTile') }}</span>
            <span class="mdi mdi-clock-alert-outline" style="color:var(--c-error);font-size:20px"></span>
          </div>
          <div class="val">{{ money(stock.receivableEvent.amount) }} ฿</div>
          <div class="t-cap" style="margin-top:8px">
            {{ $t('finance.eventReceivableCount', { count: stock.receivableEvent.count }) }}
          </div>
          <div class="t-cap">
            {{ $t('finance.eventReceivableSplit', {
              gym: money(stock.receivableEvent.gymAmount),
              organizer: money(stock.receivableEvent.organizerAmount) }) }}
          </div>
        </div>

        <div v-if="hasEventMoney && Number(stock.organizer.collected) > 0" class="scard">
          <div class="acc" style="background:linear-gradient(90deg,var(--c-info),var(--c-secondary))"></div>
          <div class="row" style="justify-content:space-between">
            <span class="lbl">{{ $t('finance.organizerTile') }}</span>
            <span class="mdi mdi-bank-transfer-out" style="color:var(--c-info);font-size:20px"></span>
          </div>
          <div class="val">{{ money(stock.organizer.outstanding) }} ฿</div>
          <div class="t-cap" style="margin-top:8px">
            {{ $t('finance.organizerCollected', { amount: money(stock.organizer.collected) }) }} ·
            {{ $t('finance.organizerRemitted', { amount: money(stock.organizer.remitted) }) }}
          </div>
          <div class="t-cap fin-warn">{{ $t('finance.organizerNotRevenue') }}</div>
        </div>

        <div class="scard fin-click" role="button" tabindex="0" @click="openDetail('deferred')"
          @keydown.enter="openDetail('deferred')">
          <div class="acc" style="background:linear-gradient(90deg,var(--c-warning),var(--c-accent))"></div>
          <div class="row" style="justify-content:space-between">
            <span class="lbl">{{ $t('finance.deferredTile') }}</span>
            <span class="mdi mdi-timer-sand" style="color:var(--c-warning);font-size:20px"></span>
          </div>
          <div class="val">{{ money(stock.deferred.amount) }} ฿</div>
          <!-- ── มุมกลับของก้อนนี้ (เจ้าของสั่ง 2026-08-04) ──
               แถบบอกว่าแพ็กเกจกลุ่มเดียวกันนี้ถูกใช้ไปแล้วกี่ % — เงินที่ถืออยู่ "สุก" แค่ไหน
               เพิ่งขาย = ภาระลากยาวอีกนาน · ใกล้ครบ = จะเคลียร์ในไม่ช้า
               ต่างจากการ์ด "รายได้ตามบริการที่สอนจริง" ซึ่งเป็นยอดตามช่วงเวลาและรวม Private ด้วย -->
          <template v-if="stock.deferred.deliveredPct !== null">
            <div class="fin-bar">
              <div class="fin-bar-fill fin-bar-deferred"
                :style="{ width: Math.min(stock.deferred.deliveredPct, 100) + '%' }"></div>
            </div>
            <div class="t-cap">
              {{ $t('finance.deferredProgress', {
                sold: money(stock.deferred.soldAmount),
                delivered: money(stock.deferred.deliveredAmount),
                pct: stock.deferred.deliveredPct }) }}
            </div>
          </template>
          <div class="t-cap" style="margin-top:8px">
            {{ $t('finance.deferredSessions', { sessions: stock.deferred.sessions }) }}
            <template v-if="stock.deferred.expiringSoonAmount > 0">
              · {{ $t('finance.deferredExpiring', { amount: money(stock.deferred.expiringSoonAmount) }) }}
            </template>
          </div>
          <div v-if="stock.deferred.monthlyUnpriceableCount" class="t-cap fin-warn">
            <span class="mdi mdi-information-outline"></span>
            {{ $t('finance.deferredMonthlyExcluded', { count: stock.deferred.monthlyUnpriceableCount }) }}
          </div>
          <div v-if="stock.deferred.unknownPriceCount" class="t-cap fin-warn">
            <span class="mdi mdi-help-circle-outline"></span>
            {{ $t('finance.deferredUnknownExcluded', {
              count: stock.deferred.unknownPriceCount, sessions: stock.deferred.unknownPriceSessions }) }}
          </div>
          <div class="fin-more"><span class="mdi mdi-magnify-plus-outline"></span> {{ $t('finance.clickDetail') }}</div>
        </div>

        <div class="scard fin-click" role="button" tabindex="0" @click="openDetail('breakage')"
          @keydown.enter="openDetail('breakage')">
          <div class="acc" style="background:linear-gradient(90deg,var(--c-text-muted),var(--c-border-strong))"></div>
          <div class="row" style="justify-content:space-between">
            <span class="lbl">{{ $t('finance.breakageTile') }}</span>
            <span class="mdi mdi-clock-alert-outline" style="color:var(--c-text-muted);font-size:20px"></span>
          </div>
          <div class="val" style="color:var(--c-text-muted)">{{ money(stock.breakage.amount) }} ฿</div>
          <div class="t-cap" style="margin-top:8px">
            {{ $t('finance.breakageDetail', { sessions: stock.breakage.sessions, count: stock.breakage.count }) }}
          </div>
          <div v-if="stock.breakage.unknownPriceCount" class="t-cap fin-warn">
            <span class="mdi mdi-help-circle-outline"></span>
            {{ $t('finance.unknownPriceExcluded', { count: stock.breakage.unknownPriceCount }) }}
          </div>
          <div class="fin-more"><span class="mdi mdi-magnify-plus-outline"></span> {{ $t('finance.clickDetail') }}</div>
        </div>
      </div>

      <!-- ══ C. เทรนด์รายเดือน ══ -->
      <div class="fin-sec"><span class="mdi mdi-chart-bar"></span> {{ $t('finance.sectionTrend') }}</div>
      <div class="scard">
        <!-- แท่งเดียวต่อเดือน ซ้อนกัน: ค่าคอร์ส + ค่าธรรมเนียม = ลูกค้าจ่ายรวม (= เงินเข้าบัญชี)
             ฐานเดียวกับการ์ดยอดขายข้างบนเป๊ะ ๆ — เจ้าของเคยจับได้ว่าการ์ดกับกราฟไม่ตรงกัน
             เพราะการ์ดใช้ price อย่างเดียวแต่กราฟรวม fee (แก้แล้ว 2026-08-04)
             ตัวเลขพิมพ์ไว้บนแท่งเลย ไม่ต้องเอาเมาส์ชี้ · กดแท่งเพื่อดูรายการของเดือนนั้น -->
        <div class="fin-legend">
          <span><i class="sw" style="background:var(--c-success)"></i> {{ $t('finance.legendCourse') }}</span>
          <span><i class="sw" style="background:var(--c-warning)"></i> {{ $t('finance.legendFee') }}</span>
          <span class="fin-legend-eq">= {{ $t('finance.legendGrossAll') }}</span>
        </div>
        <div class="fin-chart">
          <button v-for="m in trendMonths" :key="m.ym" class="fin-col" v-hint="barTitle(m)"
            type="button" :aria-disabled="!m.gross ? 'true' : null" @click="openMonthDetail(m)">
            <div class="fin-bar-val" v-if="m.gross">{{ moneyShort(m.gross) }}</div>
            <div class="fin-bars">
              <div class="fin-stack">
                <div class="fin-seg-fee" :style="{ height: barH(m.fee) + 'px' }"></div>
                <div class="fin-seg-net" :style="{ height: barH(m.course) + 'px' }"></div>
              </div>
            </div>
            <div class="fin-col-label">{{ monthLabel(m.ym) }}</div>
            <div class="fin-col-sub" v-if="m.fee > 0">+{{ moneyShort(m.fee) }}</div>
          </button>
        </div>
        <div class="fin-note">
          <span class="mdi mdi-information-outline"></span>
          {{ $t('finance.trendNoteRealOnly', { date: formatDateTH(statsSince) }) }}
        </div>
      </div>

      <!-- ══ D. Breakdown ══ -->
      <div class="fin-grid3">
        <div class="scard">
          <div class="lbl" style="margin-bottom:8px">{{ $t('finance.byProgram') }}</div>
          <div class="fin-mini-scroll">
            <table class="idt fin-mini">
              <tbody>
                <tr v-for="r in breakdown.byProgram" :key="r.key">
                  <td>{{ r.key }}</td>
                  <td class="num">{{ money(r.gross) }} ฿</td>
                  <td class="num dim">{{ pctOf(r.gross, flow.sales.gross) }}</td>
                </tr>
                <tr v-if="!breakdown.byProgram.length"><td colspan="3" class="dim">{{ $t('finance.noData') }}</td></tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="scard">
          <div class="lbl" style="margin-bottom:8px">{{ $t('finance.byMethod') }}</div>
          <div class="fin-mini-scroll">
            <!-- ค่าธรรมเนียมเป็นรายรับ ไม่ใช่ยอดที่ถูกหัก — จึงไม่มีคอลัมน์ "สุทธิ" และไม่มีเครื่องหมายลบ
                 คอลัมน์ "เข้าบัญชี" คือยอดเต็มที่ลูกค้าจ่าย (รวมค่าธรรมเนียมแล้ว) -->
            <table class="idt fin-mini">
              <thead><tr><th></th><th class="num">{{ $t('finance.colIntoAccount') }}</th><th class="num">{{ $t('finance.colFeePart') }}</th></tr></thead>
              <tbody>
                <tr v-for="r in breakdown.byMethod" :key="r.key">
                  <td>{{ methodLabel(r.key) }}</td>
                  <td class="num">{{ money(r.gross) }}</td>
                  <td class="num dim">{{ r.fee > 0 ? money(r.fee) : '—' }}</td>
                </tr>
                <tr v-if="!breakdown.byMethod.length"><td colspan="3" class="dim">{{ $t('finance.noData') }}</td></tr>
              </tbody>
            </table>
          </div>
        </div>

        <div class="scard">
          <div class="lbl" style="margin-bottom:8px">{{ $t('finance.priceAndDiscount') }}</div>
          <div class="fin-mini-scroll">
            <table class="idt fin-mini">
              <tbody>
                <tr><td>{{ $t('finance.promoPackages') }}</td>
                  <td class="num">{{ breakdown.discount.promoCount }} / {{ breakdown.discount.salesCount }}</td></tr>
                <tr><td>{{ $t('finance.totalDiscount') }}</td>
                  <td class="num" :class="{ 'fin-danger': breakdown.discount.totalDiscount > 0 }">{{ money(breakdown.discount.totalDiscount) }} ฿</td></tr>
                <tr><td>{{ $t('finance.avgDiscountPct') }}</td>
                  <td class="num">{{ breakdown.discount.avgDiscountPct === null ? '—' : breakdown.discount.avgDiscountPct + '%' }}</td></tr>
                <tr><td class="dim">{{ $t('finance.comparedAgainstList') }}</td>
                  <td class="num dim">{{ breakdown.discount.comparedCount }} {{ $t('finance.unitPackages') }}</td></tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- ══ E. ค้างรับ ══ -->
      <div class="fin-sec" ref="receivablesSec"><span class="mdi mdi-account-alert-outline"></span> {{ $t('finance.sectionReceivables') }}</div>
      <div class="scard">
        <div class="fin-aging">
          <span class="badge badge-success">{{ $t('finance.aging0') }} · {{ money(receivables.aging['0-30']) }} ฿</span>
          <span class="badge badge-warning">{{ $t('finance.aging31') }} · {{ money(receivables.aging['31-60']) }} ฿</span>
          <span class="badge badge-error">{{ $t('finance.aging61') }} · {{ money(receivables.aging['61-90']) }} ฿</span>
          <span class="badge badge-error">{{ $t('finance.aging90') }} · {{ money(receivables.aging['90+']) }} ฿</span>
          <!-- แถวที่ไม่รู้ราคาไม่ได้อยู่ในบัคเก็ตใดข้างบนเลย ถ้าไม่บอกไว้ ผลรวม 4 ช่องจะดูเหมือน
               เป็นยอดค้างรับทั้งหมด ทั้งที่ยังมีอีกกี่รายที่ตีเป็นเงินไม่ได้ -->
          <span v-if="receivables.unknownPriceCount" class="badge badge-neutral">
            {{ $t('finance.unknownPrice') }} · {{ receivables.unknownPriceCount }} {{ $t('finance.unitPackages') }}
          </span>
        </div>
        <div style="overflow-x:auto">
          <table class="idt">
            <thead>
              <!-- เจ้าของสั่ง 2026-08-04: ใช้เลขที่แพ็กเกจแทนเบอร์ผู้ปกครอง — เลขที่แพ็กเกจเอาไป
                   ค้นต่อในหน้าคอร์สลูกค้าได้ทันที ส่วนเบอร์โทรถูกตัดออกจาก API ไปด้วย ไม่ใช่แค่ซ่อน -->
              <tr>
                <th>{{ $t('finance.colRefer') }}</th>
                <th>{{ $t('finance.colStudents') }}</th>
                <th>{{ $t('finance.colCourse') }}</th>
                <th class="num">{{ $t('finance.colAmount') }}</th>
                <th class="num">{{ $t('finance.colOverdue') }}</th>
                <th class="num">{{ $t('finance.colUsedUnpaid') }}</th>
              </tr>
            </thead>
            <tbody v-if="loadingReceivables">
              <tr v-for="n in 5" :key="'rs' + n"><td colspan="6"><div class="id-skel" style="height:18px"></div></td></tr>
            </tbody>
            <tbody v-else class="id-fade-in" :key="'rc' + receivablePage">
              <tr v-for="r in receivables.items" :key="r.courserefer">
                <td>{{ r.courserefer }}</td>
                <td>{{ r.students || '—' }}</td>
                <td>{{ r.coursename }}</td>
                <td class="num">
                  <span v-if="r.amount !== null">{{ money(r.amount) }} ฿</span>
                  <span v-else class="fin-danger">{{ $t('finance.unknownPrice') }}</span>
                </td>
                <td class="num" :class="overdueClass(r.daysOverdue)">{{ r.daysOverdue }} {{ $t('finance.unitDays') }}</td>
                <td class="num" :class="{ 'fin-danger': r.sessionsUsedUnpaid > 0 }">
                  {{ r.sessionsUsedUnpaid }} {{ $t('finance.unitTimes') }}
                </td>
              </tr>
              <tr v-if="!receivables.items.length"><td colspan="6" class="dim">{{ $t('finance.noReceivables') }}</td></tr>
            </tbody>
          </table>
        </div>
        <id-grid-footer v-if="receivables.total > 0"
          :page="receivablePage"
          :page-count="Math.max(1, Math.ceil(receivables.total / receivablePerPage))"
          :per-page="receivablePerPage" :total="receivables.total"
          @update:page="p => { receivablePage = p; fetchReceivables(true); }"
          @update:per-page="n => { receivablePerPage = n; receivablePage = 1; fetchReceivables(true); }" />
      </div>

      <!-- ══ F. Private แยกตามโค้ช ══
           ⚠️ ตัวเลข "ค้างจ่าย" ในหัวข้อนี้เป็น flow (คาบที่สอนในช่วงที่เลือก) จึงไม่เท่ากับการ์ด
           "ค้างรับ Private" ใน §B ที่เป็น stock (ณ วันนี้ ทุกช่วงเวลา) — คนละนิยามโดยตั้งใจ
           ป้ายกำกับข้างล่างต้องเขียนให้ชัดเสมอ ไม่งั้นอ่านแล้วนึกว่าตัวเลขขัดกันเอง -->
      <div class="fin-sec"><span class="mdi mdi-whistle-outline"></span> {{ $t('finance.sectionPrivateCoach') }}</div>
      <div class="scard" style="padding:16px">
        <div class="t-cap" style="margin-bottom:10px">{{ $t('finance.privateCoachHint') }}</div>

        <div v-if="loadingPrivate" style="padding:4px 0">
          <div class="id-skel" style="height:18px;margin:6px 0"></div>
          <div class="id-skel" style="height:18px;margin:6px 0"></div>
          <div class="id-skel" style="height:18px;margin:6px 0"></div>
        </div>
        <!-- "โหลดพลาด" ต้องแยกจาก "ไม่มีข้อมูล" — ในหน้าการเงิน ศูนย์กับโหลดไม่ได้อ่านคนละความหมาย -->
        <div v-else-if="privateError" class="dim" style="padding:8px 0">
          <span class="mdi mdi-alert-circle-outline"></span> {{ $t('finance.privLoadError') }}
          <button type="button" class="id-btn id-btn-ghost" style="margin-left:8px" @click="fetchPrivateByCoach()">{{ $t('finance.privLoadRetry') }}</button>
        </div>
        <div v-else-if="!privateData.totals || !privateData.totals.sessions" class="dim" style="padding:8px 0">
          {{ $t('finance.noPrivateInRange') }}
        </div>
        <template v-else>
          <!-- สรุปรวมทั้งช่วง -->
          <div class="fin-priv-tot">
            <div><div class="lbl">{{ $t('finance.privSessions') }}</div><div class="dq-val">{{ privateData.totals.sessions }}</div></div>
            <div><div class="lbl">{{ $t('finance.privHours') }}</div><div class="dq-val">{{ privateData.totals.hours }}</div></div>
            <div><div class="lbl">{{ $t('finance.privRevenue') }}</div><div class="dq-val">{{ $money(privateData.totals.revenue) }}</div></div>
            <div><div class="lbl">{{ $t('finance.privCollected') }}</div><div class="dq-val">{{ $money(privateData.totals.collected) }}</div></div>
            <div><div class="lbl">{{ $t('finance.privUnpaidInRange') }}</div><div class="dq-val fin-warn">{{ $money(privateData.totals.unpaidAmount) }}</div></div>
            <div><div class="lbl">{{ $t('finance.privRatePerHour') }}</div><div class="dq-val">{{ privateData.totals.ratePerHour === null ? '—' : $money(privateData.totals.ratePerHour) }}</div></div>
          </div>
          <div v-if="privateData.totals.feeUnknownSessions" class="t-cap fin-warn" style="margin-top:6px">
            <span class="mdi mdi-alert-outline"></span>
            {{ $t('finance.privFeeUnknown', { n: privateData.totals.feeUnknownSessions }) }}
          </div>
          <div v-if="privateData.totals.priceUnknownSessions" class="t-cap fin-warn" style="margin-top:6px">
            <span class="mdi mdi-alert-outline"></span>
            {{ $t('finance.privPriceUnknown', { n: privateData.totals.priceUnknownSessions }) }}
          </div>

          <!-- สลับมุมมอง: รายเดือน×โค้ช / รวมต่อโค้ช / ต่อประเภท -->
          <div class="pb-modes" style="margin-top:14px">
            <button v-for="m in privateViews" :key="m.key" type="button"
              class="pb-mode" :class="{ 'is-active': privateView === m.key }" @click="privateView = m.key">{{ m.label }}</button>
          </div>

          <div class="fin-tbl-wrap" style="margin-top:10px">
            <table class="idt">
              <thead>
                <tr>
                  <th v-if="privateView === 'monthly'">{{ $t('finance.colMonth') }}</th>
                  <th v-if="privateView !== 'type'">{{ $t('finance.colCoach') }}</th>
                  <th v-if="privateView === 'type'">{{ $t('finance.colPrivateType') }}</th>
                  <th style="text-align:center">{{ $t('finance.privSessions') }}</th>
                  <th style="text-align:center">{{ $t('finance.privHours') }}</th>
                  <th style="text-align:right">{{ $t('finance.privRevenue') }}</th>
                  <th style="text-align:right">{{ $t('finance.privCollected') }}</th>
                  <th style="text-align:right">{{ $t('finance.privUnpaidInRange') }}</th>
                  <th style="text-align:right">{{ $t('finance.privRatePerHour') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(r, i) in privateRows" :key="i">
                  <td v-if="privateView === 'monthly'">{{ r.ym }}</td>
                  <td v-if="privateView !== 'type'">{{ r.coachname }}</td>
                  <td v-if="privateView === 'type'">{{ privateTypeLabel(r.privatetype) }}</td>
                  <td style="text-align:center">{{ r.sessions }}</td>
                  <td style="text-align:center">{{ r.hours }}</td>
                  <td style="text-align:right">{{ $money(r.revenue) }}</td>
                  <td style="text-align:right">{{ $money(r.collected) }}</td>
                  <td style="text-align:right" :class="r.unpaidAmount > 0 ? 'fin-warn' : ''">{{ $money(r.unpaidAmount) }}</td>
                  <td style="text-align:right">{{ r.ratePerHour === null ? '—' : $money(r.ratePerHour) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="t-cap" style="margin-top:8px">{{ $t('finance.privNotProfit') }}</div>
        </template>
      </div>

      <!-- ══ G. ความครบถ้วนของข้อมูล ══ -->
      <div class="fin-sec"><span class="mdi mdi-database-check-outline"></span> {{ $t('finance.sectionDataQuality') }}</div>
      <div class="scard fin-dq">
        <div>
          <div class="lbl">{{ $t('finance.dqPriceActual') }}</div>
          <div class="dq-val">{{ dq.priceActual }} / {{ dq.totalPackages }}
            <span class="t-cap" :class="dq.priceActualPct >= 90 ? 'fin-ok' : 'fin-warn'">{{ dq.priceActualPct }}%</span></div>
        </div>
        <div>
          <div class="lbl">{{ $t('finance.dqMissingPrice') }}</div>
          <div class="dq-val" :class="{ 'fin-danger': dq.priceMissing > 0 }">
            {{ dq.priceMissing }}
            <button v-if="dq.priceMissing > 0" class="id-btn id-btn-ghost id-btn-sm" @click="openMissingPrice">
              <span class="mdi mdi-open-in-new"></span> {{ $t('finance.viewList') }}
            </button>
          </div>
        </div>
        <div>
          <div class="lbl">{{ $t('finance.dqEstimated') }}</div>
          <div class="dq-val">{{ dq.priceEstimated }}</div>
        </div>
        <div>
          <div class="lbl">{{ $t('finance.dqUnknownPrice') }}</div>
          <div class="dq-val" :class="{ 'fin-warn': dq.priceUnknown > 0 }">{{ dq.priceUnknown }}</div>
        </div>
        <div>
          <div class="lbl">{{ $t('finance.dqFeeUnknown') }}</div>
          <div class="dq-val" :class="{ 'fin-danger': dq.feeUnknownCount > 0 }">{{ dq.feeUnknownCount }}</div>
        </div>
        <div>
          <div class="lbl">{{ $t('finance.dqNoPaydate') }}</div>
          <div class="dq-val">{{ dq.paydateFallbackCount }}</div>
        </div>
        <div>
          <div class="lbl">{{ $t('finance.dqMonthly') }}</div>
          <div class="dq-val">{{ dq.monthlyUnpriceableCount }}</div>
        </div>
      </div>
      <div class="fin-note" style="margin-top:8px">
        <span class="mdi mdi-information-outline"></span> {{ $t('finance.notProfitNote') }}
      </div>
    </template>

    <!-- รายการที่ยังไม่ได้กรอกราคา -->
    <id-modal v-model="showMissingPrice" size="lg" icon="mdi-alert-circle-outline" :title="$t('finance.missingPriceTitle')"
      :subtitle="$t('finance.missingPriceSub', { date: formatDateTH(missingPrice.since || statsSince) })">
      <div v-if="loadingMissing" class="id-skel" style="height:120px"></div>
      <table v-else class="idt">
        <thead><tr>
          <th>{{ $t('finance.colRefer') }}</th><th>{{ $t('finance.colCourse') }}</th>
          <th>{{ $t('finance.colCreated') }}</th><th class="num">{{ $t('finance.colSuggested') }}</th>
        </tr></thead>
        <tbody>
          <tr v-for="r in missingPrice.items" :key="r.courserefer">
            <td>{{ r.courserefer }}</td>
            <td>{{ r.coursename }}</td>
            <td>{{ formatDateTH(r.createdate) }}</td>
            <td class="num">{{ r.suggestedPrice === null ? '—' : money(r.suggestedPrice) + ' ฿' }}</td>
          </tr>
          <tr v-if="!missingPrice.items.length"><td colspan="4" class="dim">{{ $t('finance.noData') }}</td></tr>
        </tbody>
      </table>
      <template #footer>
        <button class="id-btn id-btn-primary" @click="showMissingPrice = false">{{ $t('btn.close') }}</button>
      </template>
    </id-modal>

    <!-- popup เจาะลึกเบื้องหลังการ์ดแต่ละใบ — dialog เดียว เปลี่ยนคอลัมน์ตาม metric
         (เขียนแยก 6 dialog เมื่อไหร่ โครงจะหลุด sync กันแน่นอน) -->
    <id-modal v-model="showDetail" size="xl" :icon="detailIcon" :title="detailTitle" :subtitle="detailSubtitle">
      <!-- โหลดครั้งแรก และ "เปลี่ยนหน้า" ใช้ skeleton ตัวเดียวกัน ตามมาตรฐาน grid ของระบบ
           (เปลี่ยนหน้าต้องอ่านออกว่าเป็นการโหลด ไม่ใช่แค่เฟดเฉย ๆ) -->
      <div v-if="loadingDetail || detailPaging" class="id-skel" style="height:160px"></div>

      <!-- ── ระดับ 1: สรุปรายวัน (เฉพาะการ์ดเงินเข้าบัญชี — เจ้าของสั่ง 2026-08-04) ──
           เป็น "เงินที่เก็บได้จริง" ซึ่งเจ้าของดูเป็นรายวันเพื่อกระทบยอดกับธนาคาร
           จึงสรุปรายวันก่อน แล้วค่อยกดเข้าไปดูว่าวันนั้นมาจากใบไหนบ้าง -->
      <template v-else-if="isDailyView">
        <div style="overflow-x:auto">
          <table class="idt fin-detail">
            <thead>
              <tr>
                <th>{{ $t('finance.colDay') }}</th>
                <th class="num">{{ $t('finance.colDayCount') }}</th>
                <th class="num">{{ $t('finance.colIntoAccount') }}</th>
                <th class="num">{{ $t('finance.colVatInAmount') }}</th>
                <th></th>
              </tr>
            </thead>
            <tbody class="id-fade-in" :key="'dd' + detailPage">
              <tr v-for="d in pagedDailyRows" :key="d.date" class="fin-day-row" role="button" tabindex="0"
                @click="openDetailDay(d.date)" @keydown.enter="openDetailDay(d.date)">
                <td>{{ formatDateTH(d.date) }}</td>
                <td class="num">{{ d.count }}</td>
                <td class="num">{{ money(d.total) }}</td>
                <td class="num dim">{{ money(d.vat) }}</td>
                <td class="num"><span class="mdi mdi-chevron-right" style="color:var(--c-primary)"></span></td>
              </tr>
              <tr v-if="!dailyRows.length"><td colspan="5" class="dim">{{ $t('finance.noData') }}</td></tr>
            </tbody>
          </table>
        </div>
        <div class="fin-detail-total" v-if="dailyRows.length">
          {{ $t('finance.detailTotalDays', { days: dailyRows.length, count: detail.count }) }}
          <b>{{ money(detail.totalAmount) }} ฿</b>
        </div>
        <id-grid-footer v-if="dailyRows.length" :page="detailPage" :page-count="detailPageCount"
          :per-page="detailPerPage" :total="dailyRows.length"
          @update:page="goDetailPage" @update:per-page="setDetailPerPage" />
      </template>

      <!-- ── ระดับ 2 (หรือระดับเดียวสำหรับ metric อื่น): รายการรายใบ ── -->
      <template v-else>
        <button v-if="isDailyMetric" class="id-btn id-btn-ghost id-btn-sm" style="margin-bottom:10px"
          @click="backToDaily">
          <span class="mdi mdi-arrow-left"></span> {{ $t('finance.backToDaily') }}
        </button>
        <div style="overflow-x:auto">
          <table class="idt fin-detail">
            <thead>
              <tr><th v-for="c in detailCols" :key="c.key" :class="{ num: c.num }">{{ c.label }}</th></tr>
            </thead>
            <tbody class="id-fade-in" :key="'di' + detailPage + (detailDay || '')">
              <tr v-for="(r, i) in pagedItems" :key="r.refer + '-' + i">
                <td v-for="c in detailCols" :key="c.key" :class="{ num: c.num, dim: c.dim }">
                  <template v-if="c.key === 'paid'">
                    <span class="badge" :class="r.paid ? 'badge-success' : 'badge-error'">
                      {{ r.paid ? $t('finance.paidYes') : $t('finance.paidNo') }}
                    </span>
                  </template>
                  <template v-else-if="c.key === 'taught'">
                    <span class="badge" :class="r.taught ? 'badge-error' : 'badge-neutral'">
                      {{ r.taught ? $t('finance.taughtUnpaid') : $t('finance.notYetTaught') }}
                    </span>
                  </template>
                  <template v-else-if="c.key === 'usedOfTotal'">{{ r.usedSessions }} / {{ r.totalSessions }}</template>
                  <template v-else-if="c.key === 'deliveredPct'">{{ r.deliveredPct === null ? '—' : r.deliveredPct + '%' }}</template>
                  <template v-else-if="c.key === 'paymentmethod'">{{ r.paymentmethod ? methodLabel(r.paymentmethod) : '—' }}</template>
                  <template v-else-if="c.money">{{ r[c.key] === null || r[c.key] === undefined ? '—' : money(r[c.key]) }}</template>
                  <template v-else-if="c.date">{{ formatDateTH(r[c.key]) || '—' }}</template>
                  <template v-else>{{ r[c.key] || '—' }}</template>
                </td>
              </tr>
              <tr v-if="!visibleItems.length">
                <td :colspan="detailCols.length" class="dim">{{ $t('finance.noData') }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="fin-detail-total" v-if="visibleItems.length">
          {{ $t('finance.detailTotal', { count: visibleItems.length }) }}
          <b>{{ money(visibleTotal) }} ฿</b>
        </div>
        <id-grid-footer v-if="visibleItems.length" :page="detailPage" :page-count="detailPageCount"
          :per-page="detailPerPage" :total="visibleItems.length"
          @update:page="goDetailPage" @update:per-page="setDetailPerPage" />
      </template>
      <template #footer>
        <button class="id-btn id-btn-primary" @click="showDetail = false">{{ $t('btn.close') }}</button>
      </template>
    </id-modal>
  </div>
</template>

<script>
import axios from 'axios';
import moment from 'moment';
// ตัวจัดรูปแบบวันที่กลางของระบบ (Intl) — ให้ปี พ.ศ. อัตโนมัติเมื่อภาษาไทย และตามท้องถิ่นของแต่ละภาษา
// อย่าใช้ moment().format('D MMM') เอง เพราะจะได้ชื่อเดือนอังกฤษปนกับปี พ.ศ.
import { formatShortDate, monthShortNames } from '@/utils/locale';
import { formatMoney } from '@/utils/money';

// หน้ารายงานยอดขายและการเงิน — ดู C:\iStarApp\FINANCE_REPORT_DESIGN.md
// หลักการที่สะท้อนอยู่ในหน้านี้:
//   - แยก "การเคลื่อนไหวในช่วงที่เลือก" (A) ออกจาก "ยอดคงค้าง ณ วันนี้" (B) ด้วยหัวข้อคั่นชัดเจน
//     ปนกันเมื่อไหร่คนอ่านจะเข้าใจว่าค้างรับทั้งหมดเป็นของเดือนนี้ ซึ่งผิด
//   - ตัวเลขที่เป็นการประมาณ/ไม่รู้ ต้องติดป้ายทุกที่ ห้ามกลืนรวมกับตัวเลขจริง
//   - ไม่มีคำว่า "กำไร" ที่ไหนเลย ระบบไม่มีข้อมูลต้นทุน
export default {
  name: 'FinanceReport',
  data() {
    const today = moment().format('YYYY-MM-DD');
    return {
      loading: true,
      loadingReceivables: false,
      loadingMissing: false,
      exporting: false,
      reqSeq: 0,               // ลำดับคำขอสรุป — รับเฉพาะคำตอบของตัวล่าสุด (กัน race ตอนสลับช่วงเร็ว ๆ)
      receivablesLoaded: false, // ค้างรับเป็น stock โหลดครั้งเดียวพอ ไม่ต้องยิงซ้ำทุกครั้งที่เปลี่ยนช่วง
      summary: null,
      receivables: { items: [], total: 0, aging: { '0-30': 0, '31-60': 0, '61-90': 0, '90+': 0 } },
      receivablePage: 1,
      receivablePerPage: 20,
      missingPrice: { items: [], total: 0 },
      showMissingPrice: false,
      // §F Private แยกโค้ช — flow ผูกกับช่วงที่เลือก (ต่างจากตารางค้างรับที่เป็น stock)
      loadingPrivate: false,
      privateError: false,
      privateData: { monthly: [], coaches: [], types: [], totals: null, months: [] },
      privateView: 'monthly',
      // popup เจาะลึกเบื้องหลังการ์ด — dialog เดียวใช้ร่วมทุก metric
      showDetail: false,
      loadingDetail: false,
      detailMetric: '',
      detail: { items: [], count: 0, totalAmount: 0, period: null },
      // null = อยู่หน้าสรุปรายวัน · 'YYYY-MM-DD' = เจาะดูรายการของวันนั้น (ใช้เฉพาะ metric สายเงินเข้า)
      detailDay: null,
      // แบ่งหน้าในตารางของ popup — ตารางเดียวโชว์ทีละมุมมอง จึงใช้ตัวนับหน้าร่วมกันได้
      // (รีเซ็ตเป็น 1 ทุกครั้งที่สลับมุมมอง) · ค่าเริ่มต้น 20 ตามมาตรฐาน grid ของระบบ
      detailPage: 1,
      detailPerPage: 20,
      detailPaging: false,
      // ช่วงเวลาเฉพาะกิจตอนกดแท่งกราฟ (ทั้งเดือนนั้น) — ปกติใช้ช่วงที่เลือกบนหน้า
      detailRange: null,
      from: moment().startOf('month').format('YYYY-MM-DD'),
      to: today,
      activePreset: 'thisMonth',
      presets: [
        { key: 'thisMonth' }, { key: 'lastMonth' }, { key: 'last3Months' }, { key: 'thisYear' },
      ],
    };
  },
  computed: {
    // วันเริ่มเก็บสถิติของรายงาน — มาจาก backend (finance.js STATS_SINCE) ห้าม hardcode ซ้ำที่นี่
    // ใช้นอกบล็อก v-else-if="summary" ด้วย (ป้ายบนหัว + ตัวกรอง) จึงต้องกัน null ให้ครบ
    statsSince() { return this.summary ? this.summary.config.statsSince : null; },
    flow() { return this.summary.flow; },
    stock() { return this.summary.stock; },
    breakdown() { return this.summary.breakdown; },
    // การ์ดอีเวนต์ขึ้นเฉพาะเมื่อมีข้อมูลจริง (ยิมที่ยังไม่จัดอีเวนต์ไม่ต้องเห็นการ์ดว่าง)
    // กัน backend รุ่นเก่าที่ยังไม่มีคีย์ events/organizer ด้วย — ไม่งั้นหน้าพังทั้งหน้า
    hasEventMoney() {
      const f = this.flow.events, st = this.stock;
      if (!f || !st.organizer || !st.receivableEvent) return false;
      return Number(f.sales.count) > 0 || Number(f.collected.count) > 0
        || Number(f.refunds.count) > 0 || Number((f.forfeited || {}).count) > 0
        || Number(st.receivableEvent.count) > 0
        || Number(st.organizer.collected) > 0;
    },
    dq() { return this.summary.dataQuality; },
    // §F — ปุ่มสลับมุมมองของตาราง Private (เดือน×โค้ช / รวมต่อโค้ช / ต่อประเภท)
    privateViews() {
      return [
        { key: 'monthly', label: this.$t('finance.viewByMonth') },
        { key: 'coach', label: this.$t('finance.viewByCoach') },
        { key: 'type', label: this.$t('finance.viewByType') },
      ];
    },
    privateRows() {
      if (this.privateView === 'coach') return this.privateData.coaches || [];
      if (this.privateView === 'type') return this.privateData.types || [];
      return this.privateData.monthly || [];
    },
    trendMonths() { return this.summary.trend.months; },
    // สเกลแกนตั้ง — อิงยอดขายรวมของเดือนที่สูงสุด (แท่งซ้อนสูงเท่ากับ gross พอดี)
    chartMax() {
      return Math.max(...this.trendMonths.map((m) => m.gross), 1);
    },
    // ── popup เจาะลึก: หัวเรื่อง/ไอคอน/คอลัมน์ เปลี่ยนตาม metric ──
    detailTitle() {
      const map = {
        sales: this.$t('finance.salesTile'),
        collected: this.$t('finance.collectedTile'),
        vat: this.$t('finance.vatTile', { pct: this.summary ? this.summary.flow.vat.ratePct : 7 }),
        earned: this.$t('finance.earnedTile'),
        deferred: this.$t('finance.deferredTile'),
        breakage: this.$t('finance.breakageTile'),
        receivablePrivate: this.$t('finance.receivablePrivateTile'),
      };
      return map[this.detailMetric] || '';
    },
    detailSubtitle() {
      // เจาะดูวันเดียวอยู่ → บอกวันนั้นตรง ๆ ไม่ใช่ช่วงทั้งก้อน
      if (this.detailDay) return this.formatDateTH(this.detailDay);
      // stock ณ วันนี้ ไม่ผูกช่วง — ใส่ช่วงไปจะหลอกว่ากรองได้
      if (this.isStockMetric) {
        return this.$t('finance.asOfToday') + ' ' + this.formatDateTH(this.summary ? this.summary.period.today : '');
      }
      const p = this.detail.period;
      return p ? this.formatDateTH(p.from) + ' – ' + this.formatDateTH(p.to) : '';
    },
    // metric ที่เป็น "ยอดคงค้าง ณ วันนี้" — ไม่ผูกช่วงเวลาที่เลือกบนหน้า
    isStockMetric() {
      return ['deferred', 'breakage', 'receivablePrivate'].includes(this.detailMetric);
    },
    // metric ที่สรุปรายวันก่อนแล้วค่อยเจาะ — เฉพาะเงินที่เก็บได้จริง ซึ่งเจ้าของใช้กระทบยอดกับธนาคาร
    isDailyMetric() {
      return ['collected', 'vat'].includes(this.detailMetric);
    },
    // จัดกลุ่มรายวันฝั่งหน้าจอจากรายการที่โหลดมาแล้ว — ไม่ยิง API เพิ่ม และรับประกันว่า
    // ผลรวมของทุกวันเท่ากับยอดบนการ์ดเป๊ะ ๆ เพราะมาจากชุดข้อมูลเดียวกันตัวเดียว
    dailyRows() {
      const by = new Map();
      for (const r of this.detail.items || []) {
        if (!r.date) continue;
        if (!by.has(r.date)) by.set(r.date, { date: r.date, count: 0, total: 0, vat: 0 });
        const d = by.get(r.date);
        d.count += 1;
        d.total += Number(r.total || 0);
        d.vat += Number(r.vat || 0);
      }
      return Array.from(by.values()).sort((a, b) => (a.date < b.date ? 1 : -1));
    },
    // รายการที่แสดงจริงในตารางระดับล่าง — กรองเหลือวันที่เลือกเมื่ออยู่ในโหมดเจาะรายวัน
    visibleItems() {
      const items = this.detail.items || [];
      return this.detailDay ? items.filter((r) => r.date === this.detailDay) : items;
    },
    visibleTotal() {
      const key = this.detailMetric === 'vat' ? 'vat'
        : (this.detailMetric === 'sales' || this.detailMetric === 'collected') ? 'total' : 'amount';
      return this.visibleItems.reduce((a, r) => a + Number(r[key] || 0), 0);
    },
    // อยู่หน้าสรุปรายวันอยู่หรือเปล่า — ตัวตัดสินว่าตารางไหนแสดงและ footer นับจากชุดไหน
    isDailyView() {
      return this.isDailyMetric && this.detailDay === null;
    },
    detailPageCount() {
      const total = this.isDailyView ? this.dailyRows.length : this.visibleItems.length;
      return Math.max(1, Math.ceil(total / this.detailPerPage));
    },
    pagedDailyRows() {
      const start = (this.detailPage - 1) * this.detailPerPage;
      return this.dailyRows.slice(start, start + this.detailPerPage);
    },
    pagedItems() {
      const start = (this.detailPage - 1) * this.detailPerPage;
      return this.visibleItems.slice(start, start + this.detailPerPage);
    },
    detailIcon() {
      const map = {
        sales: 'mdi-cart-outline', collected: 'mdi-wallet-outline', vat: 'mdi-receipt-text-outline',
        earned: 'mdi-checkbox-marked-outline', deferred: 'mdi-timer-sand', breakage: 'mdi-clock-alert-outline',
        receivablePrivate: 'mdi-account-clock-outline',
      };
      return map[this.detailMetric] || 'mdi-magnify';
    },
    detailCols() {
      const C = {
        date: { key: 'date', label: this.$t('finance.colDate') , date: true },
        name: { key: 'name', label: this.$t('finance.colItem') },
        students: { key: 'students', label: this.$t('finance.colStudents'), dim: true },
        price: { key: 'price', label: this.$t('finance.colCourseAmount'), num: true, money: true },
        fee: { key: 'fee', label: this.$t('finance.colFee'), num: true, money: true },
        total: { key: 'total', label: this.$t('finance.colIntoAccount'), num: true, money: true },
        vat: { key: 'vat', label: this.$t('finance.colVatInAmount'), num: true, money: true },
        paid: { key: 'paid', label: this.$t('finance.colStatus') },
        method: { key: 'paymentmethod', label: this.$t('finance.colMethod') },
        sessions: { key: 'sessions', label: this.$t('finance.colSessions'), num: true },
        unitPrice: { key: 'unitPrice', label: this.$t('finance.colUnitPrice'), num: true, money: true },
        remaining: { key: 'remaining', label: this.$t('finance.colRemaining'), num: true },
        amount: { key: 'amount', label: this.$t('finance.colAmount'), num: true, money: true },
        expiredate: { key: 'expiredate', label: this.$t('finance.colExpire'), date: true },
        coach: { key: 'coach', label: this.$t('finance.colCoach') },
        taught: { key: 'taught', label: this.$t('finance.colTaughtStatus') },
        usedOfTotal: { key: 'usedOfTotal', label: this.$t('finance.colUsedOfTotal'), num: true },
        deliveredPct: { key: 'deliveredPct', label: this.$t('finance.colDeliveredPct'), num: true },
      };
      const map = {
        sales: [C.date, C.name, C.students, C.price, C.fee, C.total, C.paid],
        collected: [C.date, C.name, C.students, C.price, C.fee, C.total, C.method],
        vat: [C.date, C.name, C.total, C.vat],
        earned: [C.name, C.students, C.sessions, C.unitPrice, C.amount],
        deferred: [C.name, C.students, C.usedOfTotal, C.deliveredPct, C.remaining, C.unitPrice, C.amount, C.expiredate],
        breakage: [C.name, C.students, C.remaining, C.unitPrice, C.amount, C.expiredate],
        receivablePrivate: [C.date, C.coach, C.students, C.amount, C.taught],
      };
      return map[this.detailMetric] || [];
    },
  },
  async created() {
    const token = this.$store.getters.getToken;
    if (!token) { this.$emit('onClickChangeState', 'login'); return; }
    try {
      await axios.post(this.baseURL + '/verifyToken', {}, { headers: { Authorization: `Bearer ${token}` } });
    } catch (e) {
      this.$emit('onClickChangeState', 'login');
      return;
    }
    await this.refresh();
  },
  methods: {
    // ใช้คำแปลชุดเดียวกับหน้าจอง Private — ไม่ตั้งชื่อใหม่ ไม่งั้นระบบเรียกของเดียวกันคนละชื่อ
    privateTypeLabel(t) {
      const map = {
        '1on1': this.$t('customerCourse.private1on1'),
        '2on1': this.$t('customerCourse.private2on1'),
        '3on1': this.$t('customerCourse.private3on1'),
      };
      return map[t] || t || '—';
    },
    // ⚠️ ยอดเงินทุกจุดต้องมีทศนิยม 2 ตำแหน่งเสมอ (เจ้าของสั่ง 2026-08-05) — ต้องใส่ทั้ง
    // minimum และ maximum: ใส่แต่ maximum จะได้ 5,200 (ไม่มีทศนิยม) และ 5,200.5 (ตำแหน่งเดียว)
    // เดิมตัวนี้เป็น maximumFractionDigits: 0 = ปัดทิ้งทศนิยมทั้งหมด ทำให้ยอดบนการ์ดกับยอดจริง
    // ต่างกันได้ถึงหลักสตางค์ต่อรายการ และพอรวมกันหลายรายการก็ไม่ตรงกับผลรวมที่แสดง
    money: formatMoney,
    // ตัวเลขบนแท่งกราฟ — พื้นที่ต่อแท่งแคบ (12 เดือน) เลขเต็มจะชนกันเอง จึงย่อเป็น 12.5k / 1.2M
    moneyShort(v) {
      const n = Number(v || 0);
      if (n >= 1e6) return (Math.round(n / 1e5) / 10) + 'M';
      if (n >= 1e3) return (Math.round(n / 100) / 10) + 'k';
      return String(Math.round(n));
    },
    pctOf(part, whole) {
      if (!whole) return '—';
      return Math.round((part / whole) * 100) + '%';
    },
    formatDateTH(d) {
      return d ? formatShortDate(d) : '';
    },
    monthLabel(ym) {
      const idx = parseInt(String(ym).slice(5, 7), 10) - 1;
      return monthShortNames()[idx] || ym;
    },
    methodLabel(k) {
      const map = {
        cash: this.$t('customerCourse.paymentCash'),
        transfer: this.$t('customerCourse.paymentTransfer'),
        credit: this.$t('customerCourse.paymentCredit'),
        // คลาส Private ไม่มีคอลัมน์วิธีจ่ายในตาราง จึงแยกบัคเก็ตของตัวเอง — ต่างจาก
        // "ไม่ระบุวิธี" ของแพ็กเกจคอร์สซึ่งเกิดจากแอดมินไม่ได้เลือกและแก้ย้อนหลังได้
        __private: this.$t('finance.methodPrivate'),
      };
      return map[k] || this.$t('finance.methodUnspecified');
    },
    momClass(d) { return d > 0 ? 'badge-success' : (d < 0 ? 'badge-error' : 'badge-neutral'); },
    momIcon(d) { return d > 0 ? 'mdi-arrow-up-bold' : (d < 0 ? 'mdi-arrow-down-bold' : 'mdi-minus'); },
    overdueClass(days) {
      if (days === null) return '';
      if (days > 60) return 'fin-danger';
      if (days > 30) return 'fin-warn';
      return '';
    },
    barH(v) {
      if (!v) return 0;
      return Math.max(2, Math.round((v / this.chartMax) * 110));
    },
    barTitle(m) {
      if (!m.gross) return this.monthLabel(m.ym) + ' — ' + this.$t('finance.noData');
      return [
        this.monthLabel(m.ym),
        this.$t('finance.legendGrossAll') + ' ' + this.money(m.gross) + ' ฿',
        this.$t('finance.legendCourse') + ' ' + this.money(m.course) + ' ฿',
        this.$t('finance.legendFee') + ' ' + this.money(m.fee) + ' ฿',
        'VAT ' + this.money(m.vat) + ' ฿',
      ].join(' · ');
    },
    // ── popup เจาะลึกเบื้องหลังการ์ด ──
    // สลับมุมมองใน popup — ต้องรีเซ็ตหน้าเป็น 1 ทุกครั้ง ไม่งั้นเข้าไปดูวันที่มี 3 รายการ
    // แต่ยังค้างอยู่หน้า 2 จากมุมมองก่อนหน้า แล้วเห็นตารางว่างเปล่าโดยไม่รู้สาเหตุ
    openDetailDay(date) {
      this.detailDay = date;
      this.detailPage = 1;
    },
    backToDaily() {
      this.detailDay = null;
      this.detailPage = 1;
    },
    // เปลี่ยนหน้า/จำนวนต่อหน้าในตารางของ popup — สไลซ์ฝั่งหน้าจอทันที จึงต้องหน่วง skeleton
    // ให้ครบ 1 วิตามมาตรฐาน grid ของระบบ ไม่งั้นเปลี่ยนหน้าแล้วแว๊บจนอ่านไม่ทันว่าเปลี่ยนอะไร
    async goDetailPage(p) {
      const t0 = Date.now();
      this.detailPaging = true;
      this.detailPage = p;
      await this.$minLoad(t0);
      this.detailPaging = false;
    },
    async setDetailPerPage(n) {
      const t0 = Date.now();
      this.detailPaging = true;
      this.detailPerPage = n;
      this.detailPage = 1;
      await this.$minLoad(t0);
      this.detailPaging = false;
    },
    async openDetail(metric, range) {
      this.detailMetric = metric;
      this.detailRange = range || null;
      this.detailDay = null; // เปิดใหม่ต้องเริ่มที่หน้าสรุปเสมอ ไม่ค้างวันที่เจาะไว้จากรอบก่อน
      this.detailPage = 1;
      this.showDetail = true;
      this.loadingDetail = true;
      const token = this.$store.getters.getToken;
      try {
        const res = await axios.post(this.baseURL + '/getFinanceDetail',
          { metric, from: (range && range.from) || this.from, to: (range && range.to) || this.to },
          { headers: { Authorization: `Bearer ${token}` } });
        if (res.data.success) this.detail = res.data;
        else {
          this.showDetail = false;
          this.$emit('onErrorHandler', res.data.message || this.$t('msg.loadFail'));
        }
      } catch (error) {
        this.showDetail = false;
        this.$emit('onErrorHandler', error.message);
      } finally {
        this.loadingDetail = false;
      }
    },
    // กดแท่งกราฟ = ดูรายการขายของเดือนนั้นทั้งเดือน (กราฟเป็น 12 เดือนของตัวเอง
    // ไม่ผูกกับช่วงที่เลือกบนหน้า จึงต้องส่งช่วงของเดือนนั้นไปเอง ไม่ใช้ this.from/to)
    openMonthDetail(m) {
      if (!m.gross) return;
      const from = m.ym + '-01';
      const to = moment(from).endOf('month').format('YYYY-MM-DD');
      this.openDetail('sales', { from, to });
    },
    scrollToReceivables() {
      const el = this.$refs.receivablesSec;
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
    // ช่วงของแต่ละพรีเซ็ตแบบดิบ ๆ (ยังไม่ตัดด้วยวันเริ่มเก็บสถิติ) — ใช้ร่วมกันระหว่าง
    // ปุ่มที่กดจริงกับการตัดสินว่าปุ่มไหนควรถูกปิด ถ้าเขียนแยกกันสองที่จะหลุด sync แน่นอน
    presetRange(key) {
      const m = moment();
      if (key === 'thisMonth') {
        return { from: m.clone().startOf('month').format('YYYY-MM-DD'), to: m.format('YYYY-MM-DD') };
      }
      if (key === 'lastMonth') {
        return {
          from: m.clone().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
          to: m.clone().subtract(1, 'month').endOf('month').format('YYYY-MM-DD'),
        };
      }
      if (key === 'last3Months') {
        return { from: m.clone().subtract(2, 'months').startOf('month').format('YYYY-MM-DD'), to: m.format('YYYY-MM-DD') };
      }
      if (key === 'thisYear') {
        return { from: m.clone().startOf('year').format('YYYY-MM-DD'), to: m.format('YYYY-MM-DD') };
      }
      return null;
    },
    // ปิดเฉพาะพรีเซ็ตที่ "ทั้งช่วง" จบไปก่อนวันเริ่มเก็บสถิติ — ช่วงที่คร่อมวันตัดยังกดได้
    // (จะถูกดันขึ้นมาเริ่มที่วันตัดแทน) เพราะยังมีข้อมูลจริงให้ดูอยู่ส่วนหนึ่ง
    presetDisabled(key) {
      const r = this.presetRange(key);
      return !!(this.statsSince && r && r.to < this.statsSince);
    },
    applyPreset(key) {
      if (this.presetDisabled(key)) return;
      const r = this.presetRange(key);
      if (!r) return;
      this.activePreset = key;
      // ตัดที่ฝั่งหน้าจอด้วย ไม่รอ backend ตอบกลับ — ป้ายช่วงเวลาบนหัวข้อจะได้ไม่แว๊บ
      // เป็นช่วงที่ไม่ได้ใช้จริงก่อนแล้วค่อยเด้งกลับ
      this.from = this.statsSince && r.from < this.statsSince ? this.statsSince : r.from;
      this.to = r.to;
      this.refresh();
    },
    // แก้วันเองทีละช่อง: ระหว่างที่ยังแก้ไม่ครบคู่ ช่วงจะกลับหัวได้ (from > to) ซึ่งทำให้ทุกยอด
    // เป็น 0 แล้วดูเหมือนตัวเลขจริง — ไม่ยิงจนกว่าจะได้ช่วงที่ใช้ได้จริง
    onCustomRange() {
      this.activePreset = 'custom';
      if (this.from && this.to && this.from <= this.to) this.refresh();
    },
    async refresh() {
      this.loading = true;
      // กัน race: กดสลับช่วงเวลาเร็ว ๆ แล้วคำตอบของคำขอเก่ามาถึงทีหลัง จะเอาตัวเลขของช่วงหนึ่ง
      // ไปแปะป้ายอีกช่วงหนึ่ง — รับเฉพาะคำตอบของคำขอล่าสุดเท่านั้น
      const seq = ++this.reqSeq;
      const token = this.$store.getters.getToken;
      try {
        const res = await axios.post(this.baseURL + '/getFinanceSummary',
          { from: this.from, to: this.to },
          { headers: { Authorization: `Bearer ${token}` } });
        if (seq !== this.reqSeq) return;
        if (res.data.success) {
          this.summary = res.data.summary;
          // backend อาจดันช่วงขึ้นมาให้เริ่มที่วันเก็บสถิติ — ดึงช่วงที่ใช้จริงกลับมาแสดง
          // ไม่งั้นป้ายใต้หัวข้อจะบอกช่วงหนึ่ง แต่ตัวเลขทั้งหน้าเป็นของอีกช่วง
          this.from = this.summary.period.from;
          this.to = this.summary.period.to;
        } else {
          this.summary = null; // อย่าค้างตัวเลขเก่าไว้ใต้ป้ายช่วงเวลาใหม่
          this.$emit('onErrorHandler', res.data.message || this.$t('msg.loadFail'));
        }
      } catch (error) {
        if (seq !== this.reqSeq) return;
        this.summary = null;
        if (error.response && error.response.status === 401) {
          this.$emit('onClickChangeState', 'login');
        } else if (error.response && error.response.status === 403) {
          this.$emit('onErrorHandler', this.$t('finance.permissionDenied'));
        } else {
          this.$emit('onErrorHandler', error.message);
        }
      } finally {
        if (seq === this.reqSeq) this.loading = false;
      }
      // ตารางค้างรับเป็น stock ไม่ขึ้นกับช่วงเวลา — โหลดครั้งแรกครั้งเดียวพอ
      // (เปลี่ยนช่วงเวลาแล้วยิงซ้ำคือเปลืองเปล่า ๆ และรีเซ็ตหน้าที่ผู้ใช้เปิดอยู่ทิ้ง)
      if (seq === this.reqSeq && !this.receivablesLoaded) {
        this.receivablesLoaded = true;
        this.fetchReceivables();
      }
      // §F เป็น flow (ผูกกับช่วงที่เลือก) จึงต้องยิงใหม่ทุกครั้งที่เปลี่ยนช่วง ต่างจากตารางค้างรับ
      if (seq === this.reqSeq) this.fetchPrivateByCoach(seq);
    },
    // seq ไม่ส่ง = กด "ลองใหม่" ด้วยมือ (ช่วงเวลาไม่ได้เปลี่ยน จึงไม่ต้องเทียบกับ reqSeq)
    async fetchPrivateByCoach(seq) {
      this.loadingPrivate = true;
      this.privateError = false;
      const token = this.$store.getters.getToken;
      try {
        const res = await axios.post(this.baseURL + '/getFinancePrivateByCoach',
          { from: this.from, to: this.to },
          { headers: { Authorization: `Bearer ${token}` } });
        // กัน race แบบเดียวกับ refresh(): คำตอบของช่วงเก่าที่มาช้าห้ามทับข้อมูลช่วงปัจจุบัน
        if (seq !== undefined && seq !== this.reqSeq) return;
        if (res.data.success) {
          this.privateData = res.data;
        } else {
          // โหลดพลาดต้องล้างของเก่าทิ้ง — §F เป็น flow ผูกช่วงเวลา ตัวเลขช่วงเก่าใต้ป้ายช่วงใหม่คือข้อมูลผิด
          this.privateData = { monthly: [], coaches: [], types: [], totals: null, months: [] };
          this.privateError = true;
        }
      } catch (error) {
        if (seq !== undefined && seq !== this.reqSeq) return;
        this.privateData = { monthly: [], coaches: [], types: [], totals: null, months: [] };
        this.privateError = true;
        if (error.response && error.response.status === 401) this.$emit('onClickChangeState', 'login');
      } finally {
        if (seq === undefined || seq === this.reqSeq) this.loadingPrivate = false;
      }
    },
    async fetchReceivables(animate) {
      this.loadingReceivables = true;
      const t0 = Date.now();
      const token = this.$store.getters.getToken;
      try {
        const res = await axios.post(this.baseURL + '/getFinanceReceivables',
          { page: this.receivablePage, perPage: this.receivablePerPage },
          { headers: { Authorization: `Bearer ${token}` } });
        if (res.data.success) this.receivables = res.data;
      } catch (error) {
        // ตารางค้างรับเป็นส่วนเสริมของหน้า — โหลดไม่ได้ก็ยังดูตัวเลขสรุปด้านบนได้ ไม่ต้องเด้ง error
      } finally {
        if (animate) await this.$minLoad(t0);
        this.loadingReceivables = false;
      }
    },
    async openMissingPrice() {
      this.showMissingPrice = true;
      this.loadingMissing = true;
      const token = this.$store.getters.getToken;
      try {
        const res = await axios.post(this.baseURL + '/getFinanceMissingPrice', {},
          { headers: { Authorization: `Bearer ${token}` } });
        if (res.data.success) this.missingPrice = res.data;
      } catch (error) {
        this.$emit('onErrorHandler', error.message);
      } finally {
        this.loadingMissing = false;
      }
    },
    async exportCsv() {
      this.exporting = true;
      const token = this.$store.getters.getToken;
      try {
        const res = await axios.post(this.baseURL + '/getFinanceExport',
          { from: this.from, to: this.to },
          { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
        const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv;charset=utf-8' }));
        const a = document.createElement('a');
        a.href = url;
        a.download = `finance-${this.from}-${this.to}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        this.$emit('onErrorHandler', error.message);
      } finally {
        this.exporting = false;
      }
    },
  },
};
</script>

<style scoped>
/* ── ตัวกรองช่วงเวลา ── */
.fin-filters {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin: 12px 0 4px;
}

.fin-chip {
  font-family: var(--font-body);
  font-size: 13px;
  padding: 5px 12px;
  border-radius: var(--radius-pill);
  border: 1px solid var(--c-border);
  background: var(--c-surface);
  color: var(--c-text-body);
  cursor: pointer;
  transition: background var(--dur-fast) var(--ease), color var(--dur-fast) var(--ease);
}

.fin-chip:hover { background: var(--c-surface-3); }

/* พรีเซ็ตที่ทั้งช่วงอยู่ก่อนวันเริ่มเก็บสถิติ — คงปุ่มไว้ให้เห็นว่ามีตัวเลือกนี้อยู่ แต่กดไม่ได้
   (ซ่อนทิ้งจะทำให้ผู้ใช้นึกว่าระบบไม่มีพรีเซ็ตนี้ แทนที่จะรู้ว่ามีแต่ยังไม่มีข้อมูล) */
/* aria-disabled, not :disabled — a disabled control fires no mouse events, so v-hint
   could never explain WHY the chip is greyed out (both handlers guard internally) */
.fin-chip[aria-disabled='true'] {
  opacity: .42;
  cursor: not-allowed;
}

.fin-chip[aria-disabled='true']:hover { background: var(--c-surface); }

.fin-chip.on {
  background: var(--c-primary);
  border-color: var(--c-primary);
  color: var(--c-on-primary);
}

.fin-sep {
  width: 1px;
  height: 20px;
  background: var(--c-border);
  margin: 0 4px;
}

/* ── หัวข้อคั่นระหว่างกลุ่มตัวเลข — เส้นคั่นนี้คือสิ่งที่กัน "flow" ปนกับ "stock" ── */
.fin-sec {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 22px 0 10px;
  font-size: 14px;
  font-weight: 600;
  color: var(--c-text-heading);
}

.fin-sec .mdi { color: var(--c-primary); }
.fin-sec .t-cap { font-weight: 400; margin-left: auto; }

/* ── แถบสัดส่วนเงินเข้า ── */
.fin-bar {
  height: 6px;
  background: var(--c-surface-inset);
  border-radius: var(--radius-pill);
  overflow: hidden;
  margin: 8px 0 6px;
}

.fin-bar-fill {
  height: 100%;
  background: var(--c-success);
  transition: width .7s var(--ease);
}

/* แถบบนการ์ดเงินรับล่วงหน้าใช้สีเดียวกับการ์ด (เหลือง) ไม่ใช่เขียวแบบแถบ "เก็บเงินได้"
   เพราะมันคือความคืบหน้าของ "ภาระที่ต้องส่งมอบ" ไม่ใช่เรื่องดี/ไม่ดี */
.fin-bar-deferred { background: var(--c-warning); }

/* ── กราฟแท่งรายเดือน ── */
.fin-legend {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  align-items: center;
  font-size: 12px;
  color: var(--c-text-muted);
  margin-bottom: 12px;
}

.fin-legend .sw {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 3px;
  vertical-align: middle;
  margin-right: 4px;
}

.fin-legend-eq {
  color: var(--c-text-body);
  font-weight: 600;
}

/* ── กราฟแท่งซ้อน ─────────────────────────────────────────────────────────
   หนึ่งเดือน = หนึ่งแท่ง: เข้าบัญชีจริง (ล่าง) + ค่าธรรมเนียม (บน) = ยอดขายรวม
   ทำให้อ่านได้ทันทีว่าขายได้เท่าไหร่ และโดนค่าธรรมเนียมกินไปเท่าไหร่ */
.fin-chart {
  display: flex;
  align-items: flex-end;
  gap: 6px;
  min-height: 150px;
}

/* แท่งเดือนเป็น <button> เพื่อให้กดดูรายการของเดือนนั้นได้ (คีย์บอร์ดก็กดได้) — ล้างหน้าตา
   ปุ่มทิ้งให้เหลือแค่คอลัมน์กราฟเหมือนเดิม */
.fin-col {
  flex: 1;
  text-align: center;
  min-width: 0;
  background: none;
  border: 0;
  padding: 0;
  font-family: var(--font-body);
  cursor: pointer;
  border-radius: var(--radius-sm);
  transition: background var(--dur-fast) var(--ease);
}

.fin-col:hover:not([aria-disabled='true']) { background: var(--c-surface-3); }
.fin-col[aria-disabled='true'] { cursor: default; }

/* ตัวเลขพิมพ์บนแท่งเลย — เจ้าของบอกว่าต้องเอาเมาส์ชี้ถึงเห็นตัวเลข = ใช้งานยาก */
.fin-bar-val {
  font-size: 11px;
  font-weight: 600;
  color: var(--c-text-heading);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  margin-bottom: 2px;
}

.fin-col-sub {
  font-size: 10px;
  color: var(--c-warning-tx);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.fin-bars {
  display: flex;
  align-items: flex-end;
  justify-content: center;
  height: 130px;
}

.fin-stack {
  position: relative;
  width: 100%;
  max-width: 34px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
}

.fin-seg-net,
.fin-seg-fee {
  width: 100%;
  transition: height .5s var(--ease);
}

.fin-seg-net {
  background: var(--c-success);
  border-radius: 0 0 3px 3px;
}

.fin-seg-fee {
  background: var(--c-warning);
  border-radius: 3px 3px 0 0;
}

.fin-col-label {
  font-size: 11px;
  color: var(--c-text-body);
  margin-top: 6px;
  white-space: nowrap;
}

.fin-note {
  display: flex;
  gap: 6px;
  align-items: flex-start;
  margin-top: 12px;
  padding: 8px 10px;
  background: var(--c-warning-bg);
  color: var(--c-warning-tx);
  border-radius: var(--radius-sm);
  font-size: 12px;
  line-height: 1.6;
}

/* หมายเหตุขอบเขตข้อมูลของทั้งหน้า — เป็น "ข้อเท็จจริงของรายงาน" ไม่ใช่คำเตือนว่ามีอะไรผิดพลาด
   จึงใช้โทนข้อมูล ไม่ใช่เหลืองเตือนแบบ .fin-note ตัวอื่น ๆ ที่บอกว่าตัวเลขมีข้อจำกัด */
.fin-note-scope {
  margin-top: 10px;
  background: var(--c-info-bg);
  color: var(--c-info-tx);
}

/* ── การ์ด breakdown 3 ใบ ── */
/* แถวการ์ดสรุปของหน้านี้มี 3 ใบ — เดิมใช้ .stat-grid ซึ่งเป็น repeat(5,1fr) ของแดชบอร์ด
   ทำให้การ์ด 3 ใบกินแค่ 3 ใน 5 คอลัมน์ แล้วเหลือช่องว่างค้างด้านขวา */
.fin-stat-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
}

/* แถว flow มี 4 ใบ (เพิ่มการ์ด VAT 2026-08-04) — จอกลางพับเป็น 2×2 ก่อนค่อยเรียงเดี่ยว
   ไม่ใช้ auto-fit เพราะอยากให้ 4 ใบพับเป็นคู่เสมอ ไม่ใช่ 3+1 เก้ ๆ กัง ๆ */
.fin-stat-row.cols4 { grid-template-columns: repeat(4, 1fr); }

@media (max-width: 1200px) {
  .fin-stat-row.cols4 { grid-template-columns: repeat(2, 1fr); }
}

@media (max-width: 900px) {
  .fin-stat-row { grid-template-columns: 1fr; }
}

@media (max-width: 640px) {
  .fin-stat-row.cols4 { grid-template-columns: 1fr; }
}

/* การ์ดที่กดดูรายละเอียดได้ — ยกตัวนิด ๆ ตอน hover + มีบรรทัดชวนกดมุมล่าง
   (ถ้าไม่มีตัวบอก ผู้ใช้ไม่มีทางรู้ว่าการ์ดกดได้) */
.fin-click { cursor: pointer; }

.fin-click:hover {
  box-shadow: var(--shadow-lg, 0 6px 18px rgba(0, 0, 0, .10));
  transform: translateY(-1px);
  transition: box-shadow var(--dur-fast) var(--ease), transform var(--dur-fast) var(--ease);
}

.fin-more {
  margin-top: 8px;
  font-size: 11px;
  color: var(--c-text-muted);
  display: flex;
  align-items: center;
  gap: 4px;
}

.fin-click:hover .fin-more { color: var(--c-primary); }

/* ── popup เจาะลึก ── */
.fin-detail td, .fin-detail th { padding: 6px 8px; font-size: 13px; }

.fin-detail-total {
  margin-top: 10px;
  text-align: right;
  font-size: 13px;
  color: var(--c-text-body);
}

.fin-detail-total b { font-size: 15px; color: var(--c-text-heading); margin-left: 6px; }

/* แถวสรุปรายวันกดเจาะเข้าไปดูรายการของวันนั้นได้ — ต้องดูออกว่ากดได้ ไม่ใช่ตารางอ่านอย่างเดียว */
.fin-day-row { cursor: pointer; }
.fin-day-row:hover { background: var(--c-surface-3); }

/* .scard เป็น overflow:hidden และ .idt เป็น white-space:nowrap → ถ้าไม่มี wrapper นี้
   คอลัมน์ขวาของตารางจะถูกตัดหายไปเงียบ ๆ เลื่อนดูไม่ได้เลยตอนการ์ดแคบ
   (มาตรฐาน grid ของระบบกำหนดให้ตัวห่อต้องมี overflow-x:auto เสมอ) */
.fin-mini-scroll {
  overflow-x: auto;
  margin: 0 -4px;
  padding: 0 4px;
}

/* §F ต้องมีตัวห่อ overflow-x:auto ด้วยเหตุผลเดียวกับ .fin-mini-scroll ข้างบน —
   ก่อนหน้านี้คลาสนี้ถูกใช้ใน template โดยไม่มีนิยาม ตารางเลยโดน .scard ตัดคอลัมน์ขวาทิ้งเงียบ ๆ */
.fin-tbl-wrap {
  overflow-x: auto;
  margin: 0 -4px;
  padding: 0 4px;
}

/* การ์ดสรุป 6 ค่าของ §F — grid เดียวกับ .fin-dq */
.fin-priv-tot {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 14px;
}

.fin-grid3 {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 12px;
  margin-top: 12px;
}

.fin-mini td, .fin-mini th { padding: 5px 6px; font-size: 13px; }
.fin-mini .num { text-align: right; font-variant-numeric: tabular-nums; }
.fin-mini .dim, .idt .dim { color: var(--c-text-muted); }

/* ── ค้างรับ ── */
.fin-aging {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  margin-bottom: 12px;
}

.idt .num { text-align: right; font-variant-numeric: tabular-nums; }

/* ── ความครบถ้วนของข้อมูล ── */
.fin-dq {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 14px;
}

.fin-dq .dq-val,
.fin-priv-tot .dq-val {
  font-size: 17px;
  font-weight: 600;
  color: var(--c-text-heading);
  margin-top: 2px;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

/* ── สีสถานะที่ใช้ร่วมกันทั้งหน้า ── */
.fin-danger { color: var(--c-error) !important; }
.fin-warn { color: var(--c-warning-tx) !important; }
.fin-ok { color: var(--c-success-tx) !important; }
</style>
